import { createContext, useContext } from "@bestowinc/tinkertoy-shell-deps/react";
import type { ReactNode } from "@bestowinc/tinkertoy-shell-deps/react";

import type { VersionString, ViewID } from "@bestowinc/tinkertoy-core";

/**
 * Per-view identity tuple. Captured at the top of the rendered view tree
 * so action-call hooks (e.g. {@link useClientActionMap}) can forward the
 * right identity on every request without callers having to thread it
 * through their components.
 *
 * The values are constant for the lifetime of a single rendered view; a
 * new view mount produces a fresh provider — matching the design contract
 * that interaction handlers are NOT shared across views.
 *
 * Shell-static config (e.g. the action endpoint's base URL) lives in
 * {@link ShellRuntimeConfig} instead, so it isn't redeclared per view.
 */
export type ViewIdentity = {
  /** Stable view ID from the loader response. */
  viewId: ViewID;
  /** Exact loader-resolved version of the view bundle being rendered. */
  viewVersion: VersionString;
};

const ViewIdentityContext = createContext<ViewIdentity | null>(null);

export interface ViewIdentityProviderProps {
  value: ViewIdentity;
  children: ReactNode;
}

/**
 * Provides {@link ViewIdentity} to the rendered view subtree. Rendered by
 * `<ViewRenderer>`; tests that mount module-renderer components in
 * isolation can wrap their fixtures with this provider directly.
 *
 * Callers are expected to pass a stable `value` reference (memoize on the
 * primitive fields) so consumers' `useMemo` dependencies don't fire on
 * every parent render.
 */
export function ViewIdentityProvider({
  value,
  children,
}: ViewIdentityProviderProps) {
  return (
    <ViewIdentityContext.Provider value={value}>
      {children}
    </ViewIdentityContext.Provider>
  );
}

/**
 * Read the current view's {@link ViewIdentity} from context.
 *
 * @throws {Error} when called outside a {@link ViewIdentityProvider},
 * which usually means a component that issues action POSTs (e.g. one
 * using `useClientActionMap`) was rendered outside of a `<ViewRenderer>`.
 */
export function useViewIdentity(): ViewIdentity {
  const ctx = useOptionalViewIdentity();
  if (ctx === null) {
    throw new Error(
      "useViewIdentity() must be called inside a <ViewIdentityProvider>. " +
        "This usually means a component that issues action POSTs (e.g. one " +
        "using useClientActionMap) was rendered outside of a <ViewRenderer>.",
    );
  }
  return ctx;
}

/**
 * {@link useViewIdentity} without the provider requirement — `null` outside a
 * {@link ViewIdentityProvider}, e.g. for a layout rendered before its view
 * has committed.
 */
export function useOptionalViewIdentity(): ViewIdentity | null {
  return useContext(ViewIdentityContext);
}
